import { useEffect, useRef, useState, useCallback } from 'react'

const MAX_PERCENTAGE = 96
const FINISH_DEFAULT_TIME = 1500

/**
 * @prop value {number} Current percentage (value) of the progress bar.
 * @prop increase {Function} Increases the percentage (value) of the progress bar.
 * @prop start {Function} Sets the starting percentage of the progress bar.
 * @prop start {Function} Sets the percentage of the progress bar to 100.
 */
type UseProgress = {
  progress: number
  increase: (value: number) => void
  start: (value?: number) => void
  finish: (ms?: number) => void
  isEnabled: boolean
  isFinished: boolean
}

export function useFakeProgress(
  minStep: number = 10,
  maxStep: number = 20
): UseProgress {
  const [isEnabled, setEnabled] = useState(false)
  const [progress, setProgress] = useState(0)
  const percentage = useRef<number>(progress)
  const interval = useRef<number>(-1)

  percentage.current = progress

  const increase = useCallback((value: number) => {
    const INCREASED_PERCENTAGE = percentage.current + value
    setProgress(Math.min(Math.max(0, INCREASED_PERCENTAGE), MAX_PERCENTAGE))
  }, [])

  const start = useCallback((): void => {
    setProgress(1)
    setEnabled(true)
  }, [])

  const finish = useCallback((ms: number = FINISH_DEFAULT_TIME): void => {
    setProgress(100)

    window.setTimeout(() => {
      setEnabled(false)
    }, ms)
  }, [])

  useEffect((): VoidFunction => {
    if (!isEnabled) return () => void 0

    window.clearInterval(interval.current)
    interval.current = window.setInterval((): void => {
      const rnd = minStep + Math.floor(Math.random() * (maxStep - minStep + 1))
      increase(rnd)
    }, 1000)

    return (): void => {
      window.clearInterval(interval.current)
    }
  }, [maxStep, minStep, isEnabled, increase])

  return {
    progress,
    increase,
    start,
    finish,
    isEnabled,
    isFinished: progress >= 100 && !isEnabled
  }
}
